/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { Color, BufferAttribute, BufferGeometry, Vector3, ParametricGeometry } from 'three';
/**
 * Creates a buffergeometry from mathematical functions
 */
export class Graph {
    constructor() {
        this.setMinX = this.setMinU;
        this.setMaxX = this.setMaxU;
        this.setMinY = this.setMinV;
        this.setMaxY = this.setMaxV;
        this.setCountX = this.setCountU;
        this.setCountY = this.setCountV;
        this._minU = 0.0;
        this._maxU = 1.0;
        this._countU = 20;
        this._minV = 0.0;
        this._maxV = 1.0;
        this._countV = 20;
        this._colormap = [new Color(1, 1, 1)];
    }
    /**
     * sets the colormap used by the graph (i-Value)
     * @param map
     */
    setColormap(map) {
        this._colormap = map;
    }
    /**
     * number of plots inside the u domain of definition
     * @param s
     */
    setCountU(s) {
        this._countU = s;
    }
    /**
     * number of plots inside the v domain of definition
     * @param s
     */
    setCountV(t) {
        this._countV = t;
    }
    /**
     * minimum of u domain of definition
     * @param s
     */
    setMinU(s) {
        this._minU = s;
    }
    /**
     * maximum of u domain of definition
     * @param s
     */
    setMaxU(s) {
        this._maxU = s;
    }
    /**
     * minimum of v domain of definition
     * @param s
     */
    setMinV(t) {
        this._minV = t;
    }
    /**
     * maximum of v domain of definition
     * @param s
     */
    setMaxV(t) {
        this._maxV = t;
    }
    /**
     * creates a parametric surface <x, y, z (, i)> = f(u, v)
     * see : https://en.wikipedia.org/wiki/Parametric_surface
     * @param xFunc function for x-axis
     * @param yFunc function for y-axis
     * @param zFunc function for z-axis
     * @param iFunc function for i (colormap) should produce values between 0 and 1 (numbers<0 are 0, numbers>1 are 1)
     * @param meshGeometry true if you want to show that geometry as a mesh
     */
    createParametricUV(xFunc, yFunc, zFunc, iFunc, meshGeometry = true) {
        const rangeU = this._maxU - this._minU;
        const rangeV = this._maxV - this._minV;
        const meshFunction = (u0, v0, p) => {
            const u = rangeU * u0 + this._minU;
            const v = rangeV * v0 + this._minV;
            p.x = xFunc(u, v);
            p.y = yFunc(u, v);
            p.z = zFunc(u, v);
        };
        const geo = meshGeometry
            ? new ParametricGeometry(meshFunction, this._countU, this._countV)
            : this._createGeometry(meshFunction);
        if (!!iFunc && geo instanceof BufferGeometry) {
            this._createColors(iFunc, geo);
        }
        return geo;
    }
    /**
     * creates a parametric curve <x, y, z (, i)> = f(t)
     * see : https://en.wikipedia.org/wiki/Parametric_equation
     * @param xFunc function for x-axis
     * @param yFunc function for y-axis
     * @param zFunc function for z-axis
     * @param iFunc function for i (colormap) should produce values between 0 and 1 (numbers<0 are 0, numbers>1 are 1)
     */
    createParametricT(xFunc, yFunc, zFunc, iFunc) {
        const geometry = new BufferGeometry();
        const vertices = new Float32Array(this._countU * 3);
        let idx = 0;
        for (let i = 0; i < this._countU; i++) {
            const t = i / (this._countU - 1);
            vertices[idx++] = xFunc(t);
            vertices[idx++] = yFunc(t);
            vertices[idx++] = zFunc(t);
        }
        geometry.addAttribute('position', new BufferAttribute(vertices, 3));
        if (iFunc && this._colormap.length > 0) {
            const cmsize = (this._colormap.length - 1);
            const colors = new Float32Array(this._countU * 3);
            idx = 0;
            for (let i = 0; i < this._countU; i++) {
                const t = i / (this._countU - 1);
                const f = Math.max(Math.min(iFunc(t), 1.0), 0.0);
                const c = this._colormap[Math.floor(f * cmsize)];
                colors[idx++] = c.r;
                colors[idx++] = c.g;
                colors[idx++] = c.b;
            }
            geometry.addAttribute('color', new BufferAttribute(colors, 3));
        }
        return geometry;
    }
    /**
     * creates a xy-function graph
     * @param zFunc function for z-axis
     * @param iFunc function for i (colormap) should produce values between 0 and 1 (numbers<0 are 0, numbers>1 are 1)
     * @param meshGeometry true if you want to show that geometry as a mesh
     */
    createGraphXY(zFunc, iFunc, meshGeometry = true) {
        const xFunc = (x, _) => x;
        const yFunc = (_, y) => y;
        return this.createParametricUV(xFunc, yFunc, zFunc, iFunc, meshGeometry);
    }
    _createColors(func, geo) {
        if (this._colormap.length > 0) {
            const rangeU = this._maxU - this._minU;
            const rangeV = this._maxV - this._minV;
            const colors = new Float32Array(this._countU * this._countV * 3);
            const cmsize = (this._colormap.length - 1);
            let idx = 0;
            for (let vi = 0; vi < this._countV; vi++) {
                const v0 = vi / (this._countV - 1);
                for (let ui = 0; ui < this._countU; ui++) {
                    const u0 = ui / (this._countU - 1);
                    const u = rangeU * u0 + this._minU;
                    const v = rangeV * v0 + this._minV;
                    const f = Math.max(Math.min(func(u, v), 1.0), 0.0);
                    const c = this._colormap[Math.floor(f * cmsize)];
                    colors[idx++] = c.r;
                    colors[idx++] = c.g;
                    colors[idx++] = c.b;
                }
            }
            geo.addAttribute('color', new BufferAttribute(colors, 3));
        }
    }
    _createGeometry(func) {
        const geometry = new BufferGeometry();
        const vertices = new Float32Array(this._countU * this._countV * 3);
        const p = new Vector3();
        let idx = 0;
        for (let vi = 0; vi < this._countV; vi++) {
            const v = vi / (this._countV - 1);
            for (let ui = 0; ui < this._countU; ui++) {
                const u = ui / (this._countU - 1);
                func(u, v, p);
                vertices[idx++] = p.x;
                vertices[idx++] = p.y;
                vertices[idx++] = p.z;
            }
        }
        geometry.addAttribute('position', new BufferAttribute(vertices, 3));
        return geometry;
    }
}
//# sourceMappingURL=Graph.js.map